import { createApi } from '@reduxjs/toolkit/query/react';
import calculateCoolingOffEligibility from './responseUtility/coolingOffEligibility';
import calculateDaysLeftForTrialToOver from './responseUtility/daysLeftForTrialToOver';
import proxyBaseQuery from './responseUtility/proxyBaseQuery';
import {
  GET_ENTITLEMENTS,
  GET_ALL_PRODUCTS,
  GET_IS_FLEX_USER,
  UPDATE_SUBSCRIPTION_FLEX,
  CANCEL_SUBSCRIPTION_IMMEDIATE_FLEX,
  DEACTIVATE_USER,
} from '../constants/endpoints';

type UpdatePlanArgs = {
  planCode: string;
  channel: string;
  paymentAuthorizationResultToken?: string;
  changeCurrentPaymentMethod?: boolean;
  paymentMethodToken?: string;
  isAddingPaymentMethod?: boolean;
  is3dsInitiatedOnEnforcedVerification?: boolean;
};

type DeactivateFlexUserArgs = {
  CancellationType: string;
  Channel: string;
  CancellationReason: string;
};

export enum TermFrequency {
  MONTHLY = 'MONTHLY',
  ANNUALLY = 'ANNUALLY',
}

export type EntitlementsPlan = {
  planCode: string;
  isFreeTrialIncluded?: boolean;
  freeTrialEndDt?: string;
  nextBillDate?: string;
  family: string;
  termFrequency: TermFrequency;
  price: number;
};

export type EntitlementsResponse = {
  coolingOffPeriodEndDt?: string | null;
  plans: EntitlementsPlan[];
  fulfillmentStatus?: string;
  defermentInfo?: {
    type?: string;
    defermentPlanName?: string;
  };
  isInCoolingOffPeriod?: boolean;
  [key: string]: unknown;
};

type ProductPlan = {
  planCode: string;
  name?: string;
  price?: number;
  originalPrice?: number;
  discountPercentage?: number;
  trialLength?: number | null;
  family: string;
  term?: string;
};

export type ProductsResponse = {
  isTrialEligible?: boolean;
  eligiblePlans?: ProductPlan[];
};

export type IsFlexUserResponse = {
  isFlex?: boolean;
};

export const entitlementsApi = createApi({
  reducerPath: 'entitlementsApi',
  baseQuery: proxyBaseQuery,
  endpoints: builder => ({
    getEntitlements: builder.query<EntitlementsResponse, void>({
      query: () => ({ url: GET_ENTITLEMENTS }),
      transformResponse: (response: EntitlementsResponse) => {
        const plan = response.plans?.[0];
        return {
          ...response,
          family: plan?.family,
          plan,
          termFrequency: plan?.termFrequency,
          planPrice: plan?.price,
          isInCoolingOffPeriod: calculateCoolingOffEligibility(response),
          daysLeftForTrialToOver: calculateDaysLeftForTrialToOver(plan ?? {}),
        };
      },
    }),
    pollEntitlements: builder.query<EntitlementsResponse, void>({
      query: () => ({ url: GET_ENTITLEMENTS }),
      keepUnusedDataFor: 0,
    }),
    getAllProducts: builder.query<ProductsResponse, void>({
      query: () => ({ url: GET_ALL_PRODUCTS }),
      transformResponse: (response: ProductsResponse) => response,
    }),
    getIsFlexUser: builder.query<IsFlexUserResponse, void>({
      query: () => ({ url: GET_IS_FLEX_USER }),
    }),
    updatePlan: builder.mutation<unknown, UpdatePlanArgs>({
      query: args => ({
        url: UPDATE_SUBSCRIPTION_FLEX,
        method: 'PUT',
        body: args,
      }),
    }),
    downsellPlan: builder.mutation<unknown, UpdatePlanArgs>({
      query: ({ planCode, channel }) => ({
        url: CANCEL_SUBSCRIPTION_IMMEDIATE_FLEX,
        method: 'PUT',
        body: { channel, planCode },
      }),
    }),
    deactivateFlexUser: builder.mutation<unknown, DeactivateFlexUserArgs>({
      query: ({ CancellationType, Channel }) => {
        const params = [
          `channel=${encodeURIComponent(Channel)}`,
          `cancellationType=${encodeURIComponent(CancellationType)}`,
        ].join('&');
        return {
          url: `${DEACTIVATE_USER}?${params}`,
          method: 'DELETE',
        };
      },
    }),
  }),
});

export const {
  useGetEntitlementsQuery,
  useLazyPollEntitlementsQuery,
  useGetAllProductsQuery,
  useGetIsFlexUserQuery,
  useUpdatePlanMutation,
  useDownsellPlanMutation,
  useDeactivateFlexUserMutation,
} = entitlementsApi;
